import React, { useEffect, useMemo, useRef, useState } from 'react';

import { BatchUpload, JobStatusBar } from './components/viewer/BatchUpload';
import { MapperModal } from './components/viewer/MapperModal';
import { OverlayPanels } from './components/viewer/OverlayPanels';
import { QuestionMenu } from './components/viewer/QuestionMenu';
import { RecordsList } from './components/viewer/RecordsList';
import Editor from './components/math-editor/Editor';
import { boxesByPageFromComponents, fetchJson, normalizeRecord, textFromField } from './lib/mathOcrUi';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL != null ? process.env.REACT_APP_API_BASE_URL : '';

function computeSumBoxes(groups) {
  return (groups || []).reduce((s, g) => s + ((g?.boxes || []).length || 0), 0);
}

export default function App() {
  const [jobStateText, setJobStateText] = useState('');
  const [isRunningJob, setIsRunningJob] = useState(false);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [currentFileStem, setCurrentFileStem] = useState('');
  const [jobFiles, setJobFiles] = useState([]);
  const [jobStatus, setJobStatus] = useState(null);
  const [jobsList, setJobsList] = useState([]);

  const [timing, setTiming] = useState({});
  const [outputMeta, setOutputMeta] = useState('');

  const [records, setRecords] = useState([]);
  const [manualById, setManualById] = useState(new Map());
  const [answerVersion, setAnswerVersion] = useState('auto');
  const [selectedId, setSelectedId] = useState(null);

  const [originalFiles, setOriginalFiles] = useState([]);
  const [uploadFiles, setUploadFiles] = useState([]);

  const [questionMenu, setQuestionMenu] = useState({ visible: false, x: 0, y: 0, targetId: null });
  const [mapper, setMapper] = useState({ open: false, mode: null, targetId: null });

  const qPreRef = useRef(null);
  const aPreRef = useRef(null);
  const pollRef = useRef(null);

  const [editorRawValue, setEditorRawValue] = useState('');
  const [isSavingAnswer, setIsSavingAnswer] = useState(false);

  const refreshTiming = async () => {
    try {
      const t = await fetchJson(`${API_BASE_URL}/api/timing`);
      setTiming(t || {});
    } catch {
      // ignore
    }
  };

  const refreshOriginalList = async () => {
    try {
      const data = await fetchJson(`${API_BASE_URL}/api/original-pages`);
      setOriginalFiles(data.files || []);
    } catch {
      setOriginalFiles([]);
    }
  };

  const refreshUploadList = async (jobId) => {
    if (!jobId) {
      setUploadFiles([]);
      return;
    }
    try {
      const data = await fetchJson(`${API_BASE_URL}/api/upload-pages/${jobId}`);
      setUploadFiles(data.files || []);
    } catch {
      setUploadFiles([]);
    }
  };

  const refreshRecords = async (jobId, fileStem) => {
    const jobParam = jobId ? `&job_id=${jobId}` : '';
    const fileParam = fileStem ? `&file=${fileStem}` : '';
    const params = `version=auto${jobParam}${fileParam}`;
    const [autoData, manualData] = await Promise.all([
      fetchJson(`${API_BASE_URL}/api/questions?${params}`),
      fetchJson(`${API_BASE_URL}/api/questions?version=manual${jobParam}${fileParam}`),
    ]);

    const autoRecords = (autoData.records || []).map((r, i) => normalizeRecord(r, i));
    const manualRecords = (manualData.records || []).map((r, i) => normalizeRecord(r, i));
    const manualMap = new Map(manualRecords.map((r) => [r.id, r]));

    setRecords(autoRecords);
    setManualById(manualMap);

    if (manualMap.size === 0 && answerVersion === 'manual') {
      setAnswerVersion('auto');
    }

    if (selectedId && autoRecords.some((r) => r.id === selectedId)) {
      return;
    }
    const nextSel = autoRecords[0]?.id || null;
    setSelectedId(nextSel);
  };

  const selectRecord = (record) => {
    setSelectedId(record?.id || null);
  };

  const displayRecord = useMemo(() => {
    if (!selectedId) return null;
    const rec = (records || []).find((r) => r.id === selectedId);
    if (!rec) return null;
    const manual = answerVersion === 'manual' ? manualById.get(selectedId) : null;
    if (answerVersion === 'manual' && manual) {
      return { ...rec, answer: manual.answer ?? rec.answer };
    }
    return rec;
  }, [records, selectedId, answerVersion, manualById]);

  useEffect(() => {
    setEditorRawValue(displayRecord?.answer?.text || '');
  }, [displayRecord]);

  const onSaveAnswer = async () => {
    if (!displayRecord?.id) return;
    setIsSavingAnswer(true);
    try {
      await fetchJson(`${API_BASE_URL}/api/manual/set-answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target_id: String(displayRecord.id),
          answer_text: String(editorRawValue || ''),
        }),
      });
      setAnswerVersion('manual');
      await refreshRecords(currentJobId);
      setSelectedId(String(displayRecord.id));
    } catch (e) {
      window.alert(`Save failed: ${String(e)}`);
    } finally {
      setIsSavingAnswer(false);
    }
  };

  const pageRange = displayRecord?.page_range || null;
  const qGroups = useMemo(() => {
    if (!displayRecord?.question) return [];
    return boxesByPageFromComponents(displayRecord.question, pageRange?.[0] ?? 0);
  }, [displayRecord, pageRange]);
  const aGroups = useMemo(() => {
    if (!displayRecord?.answer) return [];
    return boxesByPageFromComponents(displayRecord.answer, pageRange?.[1] ?? pageRange?.[0] ?? 0);
  }, [displayRecord, pageRange]);

  const qTotal = computeSumBoxes(qGroups);
  const aTotal = computeSumBoxes(aGroups);

  const qMeta = displayRecord?.question
    ? `${qGroups.length} page(s), ${qTotal} bbox`
    : 'No question.';
  const aMeta = displayRecord?.answer ? `${aGroups.length} page(s), ${aTotal} bbox` : 'No answer.';

  const selectedJsonText = useMemo(() => {
    if (!displayRecord) return '';
    const obj = {
      page_range: displayRecord.page_range ?? null,
      question: displayRecord.question ?? null,
      answer: displayRecord.answer ?? null,
    };
    return JSON.stringify(obj, null, 2);
  }, [displayRecord]);

  useEffect(() => {
    const mj = typeof window !== 'undefined' ? window.MathJax : null;
    if (!mj?.typesetPromise) return;
    if (!qPreRef.current || !aPreRef.current) return;
    if (!displayRecord) return;
    mj.typesetPromise([qPreRef.current, aPreRef.current]).catch(() => {});
  }, [displayRecord, answerVersion]);

  useEffect(() => {
    const onDoc = () => setQuestionMenu((s) => ({ ...s, visible: false }));
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, []);

  const onManualUpdated = async ({ targetId: updatedTargetId }) => {
    setAnswerVersion('manual');
    await refreshRecords(currentJobId);
    if (updatedTargetId) setSelectedId(updatedTargetId);
  };

  // Poll job status — refresh records on every tick so per-file results show immediately
  const pollJob = async (jobId) => {
    const abortToken = {};
    pollRef.current = abortToken;
    setIsRunningJob(true);
    let lastProgress = '';
    let resolvedFileStem = ''; // stabilise fileStem during this poll session
    try {
      while (true) {
        if (pollRef.current !== abortToken) return; // aborted by new job selection
        const s = await fetchJson(`${API_BASE_URL}/api/status/${jobId}`);
        if (pollRef.current !== abortToken) return;
        setJobStatus({ id: jobId, ...s });
        setJobStateText(`Job ${jobId}: ${s.status}${s.error ? ' — ' + s.error : ''}`);
        await refreshTiming();
        // Populate jobFiles from status response (so file selector appears during processing)
        if (s.files && s.files.length > 0 && pollRef.current === abortToken) {
          const files = s.files.map((f) => f.stem || f.name || String(f));
          setJobFiles(files);
          // Auto-select first file once jobFiles are known
          if (!resolvedFileStem && files.length > 0) {
            resolvedFileStem = files[0];
            setCurrentFileStem(resolvedFileStem);
          }
        }
        // Refresh records whenever progress changes (a file finished) or job is done
        // Always use the resolved file stem to avoid flashing all files
        if (s.progress !== lastProgress && pollRef.current === abortToken) {
          lastProgress = s.progress;
          await refreshRecords(jobId, resolvedFileStem || undefined);
        }
        // Keep job list up to date with ALL jobs on every tick
        try {
          const jobsRes = await fetchJson(`${API_BASE_URL}/api/jobs`);
          setJobsList(Object.values(jobsRes.jobs || {}));
        } catch {}

        if (s.status === 'done') {
          setOutputMeta(`Wrote ${s.result ? Object.values(s.result).reduce((sum, r) => sum + (r.num_questions || 0), 0) : 0} records`);
          await refreshUploadList(jobId);
          await refreshOriginalList();
          await refreshRecords(jobId, resolvedFileStem || undefined);
          return;
        }
        if (s.status === 'error') {
          setOutputMeta('Pipeline failed — see status above.');
          return;
        }
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 1200));
      }
    } finally {
      if (pollRef.current === abortToken) setIsRunningJob(false);
    }
  };

  // Called when BatchUpload starts a new job
  const onBatchJobStarted = (jobId) => {
    setCurrentJobId(jobId);
    setCurrentFileStem('');
    setJobFiles([]);
    // Abort any previous poll loops
    pollRef.current = null;
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('mathOcrCurrentJobId', jobId);
    }
    setJobStatus({ id: jobId, status: 'processing', files: [], current_file: '', progress: '' });
    setJobStateText('');
    setOutputMeta('');
    pollJob(jobId);
  };

  // When jobFiles list changes (e.g. from pollJob), auto-select the first file
  useEffect(() => {
    if (jobFiles.length > 0 && !currentFileStem) {
      const first = jobFiles[0];
      const stem = typeof first === 'string' ? first.replace(/\.pdf$/i, '') : (first.stem || first.name || '').replace(/\.pdf$/i, '');
      if (stem) {
        setCurrentFileStem(stem);
        refreshRecords(currentJobId, stem);
      }
    }
  }, [jobFiles]);

  const onClearOutput = async () => {
    const ok = window.confirm(
      'Clear everything under output/ (uploads/ folder is kept, but its contents are deleted)?\n\nThis will delete steps/, crops/, original_pages/, timing.json, questions.jsonl, uploads_pages/, and ALL files inside uploads/.',
    );
    if (!ok) return;
    try {
      setOutputMeta('');
      setJobStateText('');
      await fetchJson(`${API_BASE_URL}/api/clear-output`, { method: 'POST' });
      setCurrentJobId(null);
      setJobStatus(null);
      setSelectedId(null);
      setRecords([]);
      setManualById(new Map());
      setOriginalFiles([]);
      setUploadFiles([]);
      setTiming({});
      setMapper({ open: false, mode: null, targetId: null });
      setQuestionMenu((s) => ({ ...s, visible: false }));
      setJobsList([]);
      setOutputMeta('Cleared output (kept uploads/).');
    } catch (e) {
      setOutputMeta(`Clear failed: ${String(e)}`);
    }
  };

  const onDeleteManual = async () => {
    const ok = window.confirm('Delete manual results and revert answer view to auto?');
    if (!ok) return;
    try {
      await fetchJson(`${API_BASE_URL}/api/manual/delete`, { method: 'POST' });
      setAnswerVersion('auto');
      await refreshRecords(currentJobId);
    } catch (e) {
      window.alert(String(e));
    }
  };

  // Re-OCR button: opens mapper in crop mode so user can draw a region
  const onEditWithOcr = () => {
    if (!displayRecord?.id) return;
    setMapper({ open: true, mode: 'crop', targetId: displayRecord.id });
  };

  // Restore state on page reload — fetch persisted jobs from API
  const restoreState = async () => {
    try {
      const jobsRes = await fetchJson(`${API_BASE_URL}/api/jobs`);
      const allJobs = jobsRes.jobs || {};
      // Include ALL jobs (done + processing + error) in the selector dropdown
      const allJobList = Object.values(allJobs);
      const doneJobs = allJobList.filter((j) => j.status === 'done');
      const processingJobs = allJobList.filter((j) => j.status === 'processing');

      setJobsList(allJobList);

      // Restore the last-selected job from localStorage
      let saved = typeof window !== 'undefined' ? window.localStorage.getItem('mathOcrCurrentJobId') : null;
      if (!saved || !allJobs[saved]) {
        saved = null;
      }
      const targetId = saved || (doneJobs.length > 0 ? doneJobs[0].id : null);
      if (targetId) {
        const job = allJobs[targetId];
        setCurrentJobId(targetId);
        setJobStatus({ id: targetId, ...job });
        // Fetch files list for the restored job
        try {
          const s = await fetchJson(`${API_BASE_URL}/api/status/${targetId}`);
          const files = (s.files || []).map((f) => f.name || f.stem || f);
          setJobFiles(files);
        } catch {}
        if (job.status === 'processing') {
          setJobStateText(`Job ${targetId}: processing`);
          pollJob(targetId);
        } else if (job.status === 'done') {
          setJobStateText(`Job ${targetId}: done`);
          setOutputMeta(
            `Wrote ${job.results ? Object.values(job.results).reduce((sum, r) => sum + (r.num_questions || 0), 0) : 0} records`,
          );
          await refreshRecords(targetId);
        } else if (job.status === 'error') {
          setJobStateText(`Job ${targetId}: error — ${job.error || ''}`);
        }
      }

      // If there's a processing job not selected, poll it
      if (processingJobs.length > 0 && !saved) {
        const procJob = processingJobs[0];
        setCurrentJobId(procJob.id);
        setJobStatus({ id: procJob.id, ...procJob });
        setJobStateText(`Job ${procJob.id}: processing`);
        pollJob(procJob.id);
      }
    } catch {
      // ignore
    }
    await refreshTiming();
    await refreshOriginalList();
  };

  useEffect(() => {
    restoreState();
  }, []);

  const questionText = textFromField(displayRecord?.question);
  const answerText = textFromField(displayRecord?.answer);

  // Replace ![img-N.jpeg](img-N.jpeg) with actual <img> tags pointing to the OCR cache API.
  const questionHtml = useMemo(() => {
    const raw = questionText || '';
    return raw.replace(
      /!\[([^\]]*)\]\(([^)]+)\)/g,
      (_, alt, src) => `<img src="${API_BASE_URL}/api/image/${encodeURIComponent(currentFileStem)}/${src}" alt="${alt}" style="max-width:100%;border-radius:6px;margin:4px 0" />`
    );
  }, [questionText, currentFileStem]);

  const manualExists = manualById.size > 0;

  return (
    <>
      <div className="container">
        <div className="header">
          <h2>Math OCR Viewer</h2>
          <div className="sub">Upload PDFs → run pipeline → inspect and edit Q/A with LaTeX</div>
        </div>

        <JobStatusBar jobStatus={jobStatus} />

        <div className="row" style={{ gap: '0.75rem', flexWrap: 'wrap' }}>
          <div className="card" style={{ flex: '1 1 auto', minWidth: '280px' }}>
            <BatchUpload apiBaseUrl={API_BASE_URL} onJobStarted={onBatchJobStarted} />
          </div>

          {(currentJobId || jobsList.length > 0) && (
            <div className="card" style={{ flex: '1 1 auto', minWidth: '280px' }}>
              <label htmlFor="jobSelect" className="muted">
                {currentJobId ? `Job ${currentJobId}` : 'Select job:'}
              </label>
              {jobsList.length > 0 && (
                <select
                  id="jobSelect"
                  value={currentJobId || ''}
                  onChange={(e) => {
                    const id = e.target.value;
                    setCurrentJobId(id);
                    setCurrentFileStem('');
                    setJobFiles([]);
                    // Abort old poll loop
                    pollRef.current = null;
                    if (id) {
                      fetchJson(`${API_BASE_URL}/api/status/${id}`).then((s) => {
                        const files = (s.files || []).map((f) => f.stem || f.name || String(f));
                        setJobFiles(files);
                      }).catch(() => {});
                      refreshUploadList(id);
                      refreshRecords(id);
                    }
                  }}
                >
                  <option value="">-- choose --</option>
                  {jobsList.map((j) => (
                    <option key={j.id} value={j.id}>
                      {j.id}{j.status === 'processing' ? ' (processing)' : ''}
                    </option>
                  ))}
                </select>
              )}
              {currentJobId && jobFiles.length >= 1 && (
                <div style={{ marginTop: '0.5rem' }}>
                  <label htmlFor="fileSelect" className="muted">Show file:</label>
                  <select
                    id="fileSelect"
                    value={currentFileStem}
                    onChange={(e) => {
                      const stem = e.target.value;
                      setCurrentFileStem(stem);
                      if (stem) {
                        refreshRecords(currentJobId, stem);
                      }
                    }}
                  >
                    {jobFiles.map((f) => {
                      const stem = typeof f === 'string' ? f.replace(/\.pdf$/i, '') : (f.stem || f.name || '').replace(/\.pdf$/i, '');
                      return <option key={stem} value={stem}>{stem}</option>;
                    })}
                  </select>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Three-column layout: left=records list, center=Q&A, right=PDF */}
        <div className="grid3" style={{ marginTop: '0.75rem' }}>
          {/* LEFT: Records list */}
          <div className="card" style={{ overflow: 'auto', maxHeight: 'calc(100vh - 240px)' }}>
            <h3>Questions</h3>
            <div className="row">
              <button
                type="button"
                onClick={async () => {
                  await refreshRecords(currentJobId);
                  await refreshTiming();
                  await refreshUploadList(currentJobId);
                  await refreshOriginalList();
                  if (records.length && !selectedId) setSelectedId(records[0].id);
                }}
              >
                Refresh
              </button>
              <button id="clearOutputBtn" type="button" className="danger" onClick={onClearOutput}>
                Clear
              </button>
            </div>
            <div id="outputMeta" className="muted" style={{ marginBottom: '0.5rem' }}>
              {outputMeta}
            </div>
            <RecordsList
              records={records}
              selectedId={selectedId}
              onSelectRecord={(r) => selectRecord(r)}
              onOpenQuestionMenu={(x, y, targetRecordId) => {
                setQuestionMenu({ visible: true, x, y, targetId: targetRecordId });
              }}
            />
          </div>

          {/* CENTER: Question + Answer */}
          <div className="card" style={{ overflow: 'auto', maxHeight: 'calc(100vh - 240px)' }}>
            <div className="qaCol">
              <div className="qaHead">Question</div>
              <div id="selectedQuestionMeta" className="muted">
                {displayRecord?.question?.page_indices?.length || 0} page(s)
              </div>
              <div
                ref={qPreRef}
                id="selectedQuestionPre"
                className="pre qaPre"
                style={{ whiteSpace: 'pre-wrap', maxHeight: '200px', overflow: 'auto' }}
                dangerouslySetInnerHTML={{ __html: questionHtml }}
              />
            </div>
            <div className="qaCol">
              <div className="qaHead">Answer</div>
              <div id="selectedAnswerMeta" className="muted">
                {displayRecord?.answer?.page_indices?.length || 0} page(s)
              </div>
              <div className="row">
                <button
                  id="saveAnswerBtn"
                  type="button"
                  onClick={onSaveAnswer}
                  disabled={isSavingAnswer || !displayRecord?.id}
                >
                  {isSavingAnswer ? 'Saving...' : 'Save'}
                </button>
                <button
                  id="editOcrBtn"
                  type="button"
                  onClick={onEditWithOcr}
                  disabled={!displayRecord?.id}
                  title="Draw a region on the original PDF page and extract LaTeX with AI"
                >
                  🤖 Edit with AI
                </button>
                <div style={{ marginLeft: '0.5rem' }}>
                  <label htmlFor="answerVersionSelect" className="muted" style={{ marginRight: '0.25rem' }}>Ver:</label>
                  <select
                    id="answerVersionSelect"
                    value={answerVersion}
                    onChange={(e) => setAnswerVersion(String(e.target.value || 'auto'))}
                    style={{ fontSize: '0.8rem' }}
                  >
                    <option value="auto">auto</option>
                    <option value="manual" disabled={!manualExists}>manual</option>
                  </select>
                </div>
                <button id="deleteManualBtn" type="button" className="danger" disabled={!manualExists} onClick={onDeleteManual} style={{ fontSize: '0.75rem', padding: '2px 6px' }}>
                  Del manual
                </button>
              </div>
              <div ref={aPreRef} id="selectedAnswerPre" className="pre qaPre" style={{ whiteSpace: 'pre-wrap' }}>
                <Editor rawValue={displayRecord?.answer?.text || ''} onRawValueChange={setEditorRawValue} />
              </div>
            </div>
          </div>

          {/* RIGHT: PDF viewer */}
          <div className="card" style={{ overflow: 'hidden', height: 'calc(100vh - 240px)', display: 'flex', flexDirection: 'column', alignSelf: 'stretch' }}>
            <h3>Uploaded PDF</h3>
            <div id="aMeta" className="muted">
              {aMeta}
            </div>
            {currentJobId && currentFileStem ? (
              <iframe
                src={`${API_BASE_URL}/pdf/uploads/${encodeURIComponent(currentFileStem + '.pdf')}`}
                title="PDF Viewer"
                style={{ flex: 1, width: '100%', border: 'none', borderRadius: '8px' }}
              />
            ) : (
              <div className="muted" style={{ padding: '1rem' }}>Select a job to view the PDF.</div>
            )}
          </div>
        </div>

        <QuestionMenu
          visible={questionMenu.visible}
          x={questionMenu.x}
          y={questionMenu.y}
          onClose={() => setQuestionMenu((s) => ({ ...s, visible: false }))}
          onChooseAnotherQuestion={() => setMapper({ open: true, mode: 'choose', targetId: questionMenu.targetId || selectedId })}
          onCropAnswer={() => setMapper({ open: true, mode: 'crop', targetId: questionMenu.targetId || selectedId })}
        />

        <MapperModal
          open={mapper.open}
          mode={mapper.mode}
          targetId={mapper.targetId}
          records={records}
          originalFiles={originalFiles}
          uploadFiles={uploadFiles}
          currentJobId={currentJobId}
          currentFileStem={currentFileStem}
          apiBaseUrl={API_BASE_URL}
          onClose={() => setMapper({ open: false, mode: null, targetId: null })}
          onManualUpdated={onManualUpdated}
        />
      </div>
    </>
  );
}